import { supportedAggregations } from '@sensehawk/query-generator/aggregations';
import IconHawkArrowDown from '~icons/hawk/arrow-down';
import IconHawkArrowUp from '~icons/hawk/arrow-up';
import IconHawkBinary from '~icons/hawk/binary';
import IconHawkBoolean from '~icons/hawk/boolean';
import IconHawkBracketsEllipses from '~icons/hawk/brackets-ellipses';
import calendar from '~icons/hawk/calendar';
import IconHawkClock from '~icons/hawk/clock';
import IconHawkFormula from '~icons/hawk/formula';
import IconHawkFunction from '~icons/hawk/function';
import IconHawkHashTwo from '~icons/hawk/hash-two';
import IconHawkInnerJoin from '~icons/hawk/inner-join';
import IconHawkOuterJoin from '~icons/hawk/outer-join';
import IconHawkRightJoin from '~icons/hawk/right-join';
import IconHawkTypeOne from '~icons/hawk/type-one';
import IconHawkMembers from '~icons/hawk/user-one';
import IconHawkLeftJoin from '~icons/hawk/vector-join';

function getIconsForType(type, resource_type) {
  const icons_type_map = {
    'string': IconHawkTypeOne,
    'text': IconHawkTypeOne,
    'members': IconHawkMembers,
    'varchar': IconHawkTypeOne,
    'char': IconHawkTypeOne,
    'numeric': IconHawkHashTwo,
    'integer': IconHawkHashTwo,
    'bigint': IconHawkHashTwo,
    'smallint': IconHawkHashTwo,
    'decimal': IconHawkHashTwo,
    'float': IconHawkHashTwo,
    'double': IconHawkHashTwo,
    'real': IconHawkHashTwo,
    'number': IconHawkHashTwo,
    'double precision': IconHawkHashTwo,
    'date': calendar,
    'datetime': calendar,
    'timestamp': calendar,
    'timestamptz': IconHawkClock,
    'boolean': IconHawkBoolean,
    'bool': IconHawkBoolean,
    'interval': IconHawkClock,
    'time': IconHawkClock,
    'binary': IconHawkBinary,
    'bytea': IconHawkBinary,
    'json': IconHawkBracketsEllipses,
    'jsonb': IconHawkBracketsEllipses,
    'uuid': IconHawkHashTwo,
    'id': IconHawkHashTwo,
    'function': IconHawkFunction,
    'formula': IconHawkFormula,
    'asc': IconHawkArrowUp,
    'desc': IconHawkArrowDown,
    'joins': {
      inner: IconHawkInnerJoin,
      outer: IconHawkOuterJoin,
      right: IconHawkRightJoin,
      left: IconHawkLeftJoin,
      full: IconHawkOuterJoin,
    },
  };
  return icons_type_map[resource_type] || icons_type_map[type] || IconHawkTypeOne;
}

const aggregations = supportedAggregations();
const supported_types = Array.from(new Set(aggregations.map(agg => agg.supportedTypes).flat()));

const operators_for_type = Array.from(supported_types).reduce((obj, type) => {
  obj[type] = aggregations.map(agg => ({
    label: agg.label,
    name: agg.name,
    output_type: agg.outputTypes[type],
    type: agg.type,
  })).filter(agg => agg.output_type);
  return obj;
}, {});

const aggregations_map = {};
aggregations.forEach((agg) => {
  aggregations_map[agg.name] = agg;
});

operators_for_type.array = [{
  label: 'Extract items',
  name: 'unnest',
  output_type: 'text',
}];
function getOperatorsForType(type) {
  return operators_for_type[type] || [];
}

const operator_name_to_label_map = aggregations.reduce((acc, operator) => {
  acc[operator.name] = operator.label;
  return acc;
}, {});

function buildHierarchy(cols) {
  const root = [];

  const findHierarchyNode = (arr, label) => arr.find(n => n.label === label && n.is_hierarchy);

  cols.forEach((col) => {
    if (Array.isArray(col.path) && col.path.length > 0) {
      let currentLevel = root;
      // Walk/create nodes for each part in the path.
      col.path.forEach((part, idx) => {
        const isLast = idx === col.path.length - 1;
        // If it's the last part, ensure there's a node and push the column into its children.
        if (isLast) {
          let node = findHierarchyNode(currentLevel, part);
          if (!node) {
            node = { label: part, is_hierarchy: true, children: [] };
            currentLevel.push(node);
          }
          node.children.push(col);
        }
        else {
          // intermediate node: ensure it exists and descend into its children
          let node = findHierarchyNode(currentLevel, part);
          if (!node) {
            node = { label: part, is_hierarchy: true, children: [] };
            currentLevel.push(node);
          }
          currentLevel = node.children;
        }
      });
    }
    else {
      // No path => top-level column
      root.push(col);
    }
  });

  return root;
}

export function useBIQueryBuilder() {
  const getColumnText = column_name => `${column_name}`;
  const getAggregationText = agg => `${agg} of `;
  const getBinText = bin => ` (${bin})`;
  const getTableText = table_name => `${table_name} ⇒ `;
  const getPathText = path => path.length > 0 ? `${path.join(' ⇒ ')} ⇒ ` : '';
  const constructFieldName = (field, selected_table_name) => field.table_name ? (`${field.table_name}→${field.label}`) : (`${selected_table_name}→${field.label}`);
  const truncateText = (text, max_length = 50) => text.length > max_length ? `${text.substring(0, max_length)}...` : text;
  const constructAlias = (fn, param) => param ? fn(truncateText(param)) : ''; // this will construct the alias for the column ${table_name} -- ${agg} ${column_name}
  const constructColumnAlias = ({ table_name, column_name, path, agg, bin }) => constructAlias(getAggregationText, agg) + constructAlias(getTableText, table_name) + constructAlias(getPathText, path) + constructAlias(getColumnText, column_name) + constructAlias(getBinText, bin);

  function createDefaultSelection() {
    return {
      columns: [],
      orderBy: [],
      limit: null,
      filters: [],
      joins: [],
    };
  }

  function getStageConfig(stages) {
    if (stages[0]?.columns?.length === 0)
      return;

    const getTableConfig = stage => ({
      table: stage.table,
      orderBy: stage.orderBy.length > 0 ? stage.orderBy : undefined,
      limit: stage.limit ? stage.limit : undefined,
      filters: stage.filters.length > 0 ? stage.filters : undefined,
      columns: stage.columns.length > 0 ? stage.columns : undefined,
      joins: stage.joins.length > 0 ? stage.joins : undefined,
      unpivot: stage.unpivot,
      ignorePreviousStageColumns: stage.ignorePreviousStageColumns,
    });
    const config = stages.map(stage => getTableConfig(stage));
    return config;
  }

  function getColumnsForSeries(series) {
    return series.generateSeries.type === 'date'
      ? [{
          name: 'date',
          type: 'date',
          label: 'date',
          alias: 'date',
          field: constructFieldName({ label: 'date', table_name: series.alias }),
          table_name: series.alias,
        }]
      : [{
          name: 'number',
          type: 'integer',
          label: 'number',
          alias: 'number',
          field: constructFieldName({ label: 'number', table_name: series.alias }),
          table_name: series.alias,
        }];
  }
  return {
    constructColumnAlias,
    constructFieldName,
    getIconsForType,
    getOperatorsForType,
    createDefaultSelection,
    buildHierarchy,
    getStageConfig,
    aggregations_map,
    getColumnsForSeries,
    operator_name_to_label_map,
  };
}
